import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSearch, FaBuilding, FaCheck, FaTimes } from "react-icons/fa";
import { useModulos } from "../../../services/hooks/useModulos";
import { useZonas } from "../../../services/hooks/useZonas";
import tiposValorizacionHoteleriaApi from "../../../services/api/tiposValorizacionHoteleriaApi";

const MantenimientoModulos = () => {
  const navigate = useNavigate();

  // Usar el hook personalizado para módulos
  const {
    modulos,
    loading,
    error,
    fetchModulosByZona,
    createModulo,
    updateModulo,
    deleteModulo,
    clearError
  } = useModulos();

  // Usar el hook de zonas para obtener las zonas disponibles
  const { zonas, fetchZonas } = useZonas();

  const [state, setState] = useState({
    moduloSeleccionado: null,
    filtro: "",
    zonaSeleccionada: null,
  });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [moduloToEdit, setModuloToEdit] = useState(null);
  const [tarifas, setTarifas] = useState([]);
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    zona_id: null,
          estado_modulo_id: 1,
    capacidad_total: 0,
    observaciones: '',
    tipo_valorizacion_id: null,
  });

  // Filtrar módulos según el filtro
  const modulosFiltrados = modulos.filter((modulo) =>
    modulo.nombre.toLowerCase().includes(state.filtro.toLowerCase()) ||
    (modulo.descripcion && modulo.descripcion.toLowerCase().includes(state.filtro.toLowerCase())) ||
    modulo.codigo.toLowerCase().includes(state.filtro.toLowerCase())
  );

  // Cargar zonas al montar el componente
  useEffect(() => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));
    if (selectedProject && selectedProject.id) {
      fetchZonas(selectedProject.id);
    }
    tiposValorizacionHoteleriaApi
      .list(true)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setTarifas(data);
      })
      .catch((err) => console.error("Error cargando tarifas:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar al montar

  // Limpiar errores cuando hay un error
  useEffect(() => {
    if (error) {
      // El error ya se mostró en el hook, solo necesitamos limpiar después
      const timer = setTimeout(() => clearError(), 5000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      codigo: '',
      nombre: '',
      descripcion: '',
      zona_id: null,
      estado_modulo_id: 1,
      capacidad_total: 0,
      observaciones: '',
      tipo_valorizacion_id: null,
    });
    setModuloToEdit(null);
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setFormData({
      codigo: '',
      nombre: '',
      descripcion: '',
      zona_id: state.zonaSeleccionada?.id || null,
      estado_modulo_id: 1,
      capacidad_total: 0,
      observaciones: '',
      tipo_valorizacion_id: null,
    });
    setModuloToEdit(null);
    setShowModal(true);
  };

  const handleOpenEditModal = (modulo) => {
    setModalMode('edit');
    setFormData({
      codigo: modulo.codigo,
      nombre: modulo.nombre,
      descripcion: modulo.descripcion || '',
      zona_id: modulo.zona_id,
      estado_id: modulo.estado_id,
      estado_modulo_id: modulo.estado_modulo_id || modulo.estado_id || 1,
      capacidad_total: modulo.capacidad_total,
      observaciones: modulo.observaciones || '',
      tipo_valorizacion_id: modulo.tipo_valorizacion_id || null,
    });
    setModuloToEdit(modulo);
    setShowModal(true);
  };

  const handleSaveModulo = async (moduloData) => {
    try {
      if (modalMode === 'create') {
        await createModulo(moduloData);
      } else {
        await updateModulo(moduloToEdit.id, moduloData);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error al guardar módulo:', error);
      // El error ya se maneja en el hook con toast
    }
  };

  const handleDeleteModulo = async (modulo) => {
    if (window.confirm(`¿Está seguro de eliminar el módulo "${modulo.nombre}"?`)) {
      try {
        await deleteModulo(modulo.id);
        setState({ ...state, moduloSeleccionado: null });
      } catch (error) {
        console.error("Error al eliminar módulo:", error);
        // El error ya se maneja en el hook con toast
      }
    }
  };

  // Función para cargar módulos de una zona específica
  const handleZonaChange = async (zonaId) => {
    if (zonaId) {
      setState({ ...state, zonaSeleccionada: zonas.find(z => z.id === zonaId) });
      await fetchModulosByZona(zonaId);
    } else {
      setState({ ...state, zonaSeleccionada: null });
      // El hook useModulos maneja el estado automáticamente
    }
  };

  return (
    <>
      {/* Digital Harmony Styles para Mantenimiento Módulos */}
      <style>
        {`
          /* Fondo degradado */
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Header principal */
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.2rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          /* Cards con Digital Harmony */
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-warning {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(251, 191, 36, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-warning:hover {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(251, 191, 36, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          /* Campos de formulario modernos */
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }
          
          /* Enhanced Table */
          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }
          
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.875rem !important;
            text-transform: uppercase !important;
            padding: 1rem 0.75rem !important;
            border: none !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }
          
          .dh-enhanced-table tbody td {
            padding: 0.75rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.9rem;
            vertical-align: middle;
          }
          
          .dh-enhanced-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%) !important;
          }
          
          .dh-enhanced-table tbody tr.selected {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%) !important;
            border-left: 4px solid #667eea;
          }
          
          /* Checkbox personalizado */
          .dh-checkbox {
            width: 20px;
            height: 20px;
            border: 2px solid #d1d5db;
            border-radius: 6px;
            background: white;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          
          .dh-checkbox:checked {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-color: #667eea;
          }
          
          /* Badges mejorados */
          .dh-badge-success {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }
          
          .dh-badge-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }

          .dh-badge-info {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }

          .dh-badge-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer />
        
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="dh-main-title">
                  <FaBuilding className="me-3" />
                  Mantenimiento de Módulos
                </h1>
                <p className="dh-main-subtitle">Gestión y administración de módulos del campamento</p>
              </div>
              <Button 
                className="btn-dh-secondary d-flex align-items-center"
                onClick={() => navigate("/home")}
              >
                <FaArrowLeft className="me-2" />
                Volver
              </Button>
            </div>
          </Container>
        </div>

        <Container fluid className="py-4">
          <Row>
            <Col lg={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Lista de Módulos</h5>
                    <div className="d-flex gap-2">
                      <Button
                        onClick={handleOpenCreateModal}
                        className="btn-dh-primary d-flex align-items-center"
                        disabled={!state.zonaSeleccionada}
                      >
                        <FaPlus className="me-2" />
                        Nuevo Módulo
                      </Button>
                      <Button
                        className="btn-dh-warning d-flex align-items-center"
                        onClick={() => {
                          if (state.moduloSeleccionado) {
                            handleOpenEditModal(state.moduloSeleccionado);
                          } else {
                            toast.error("Por favor seleccione un módulo para editar", {
                              position: "top-right",
                              autoClose: 2000
                            });
                          }
                        }}
                      >
                        <FaEdit className="me-2" />
                        Editar
                      </Button>
                      <Button
                        className="btn-dh-danger d-flex align-items-center"
                        onClick={() => handleDeleteModulo(state.moduloSeleccionado)}
                        disabled={!state.moduloSeleccionado}
                      >
                        <FaTrash className="me-2" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body style={{ padding: '2rem' }}>
                  {/* Selector de Zona */}
                  <div className="mb-4">
                    <Row>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-bold">
                            <FaBuilding className="me-2" />
                            Seleccionar Zona
                          </Form.Label>
                          <Form.Select
                            className="dh-form-control"
                            value={state.zonaSeleccionada?.id || ''}
                            onChange={(e) => handleZonaChange(parseInt(e.target.value) || null)}
                          >
                            <option value="">Seleccione una zona...</option>
                            {zonas.map((zona) => (
                              <option key={zona.id} value={zona.id}>
                                {zona.nombre}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Seleccione una zona para ver sus módulos
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        {state.zonaSeleccionada && (
                          <div className="mt-4">
                            <Badge className="dh-badge-success">
                              Zona: {state.zonaSeleccionada.nombre}
                            </Badge>
                            {state.zonaSeleccionada.descripcion && (
                              <p className="text-muted mt-2 mb-0">
                                {state.zonaSeleccionada.descripcion}
                              </p>
                            )}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>

                  {/* Campo de búsqueda */}
                  <div className="mb-4">
                    <div className="position-relative">
                      <Form.Control
                        type="search"
                        className="dh-form-control"
                        placeholder="Buscar módulo..."
                        value={state.filtro}
                        onChange={(e) => setState({ ...state, filtro: e.target.value })}
                        style={{ paddingLeft: '3rem' }}
                        disabled={!state.zonaSeleccionada}
                      />
                      <FaSearch 
                        className="position-absolute" 
                        style={{ 
                          left: '1rem', 
                          top: '50%', 
                          transform: 'translateY(-50%)', 
                          color: '#6b7280' 
                        }} 
                      />
                    </div>
                  </div>

                  <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <Table className="dh-enhanced-table">
                      <thead>
                        <tr>
                          <th style={{ width: "50px" }}></th>
                          <th>Código</th>
                          <th>Nombre</th>
                          <th>Descripción</th>
                          <th>Tarifa valorización</th>
                          <th>Capacidad</th>
                          <th className="text-center">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <div className="d-flex justify-content-center align-items-center">
                                <div className="spinner-border text-primary me-3" role="status">
                                  <span className="visually-hidden">Cargando...</span>
                                </div>
                                <span>Cargando módulos...</span>
                              </div>
                            </td>
                          </tr>
                        ) : !state.zonaSeleccionada ? (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <Alert variant="info" className="mb-0">
                                <FaBuilding className="me-2" />
                                Seleccione una zona para ver sus módulos
                              </Alert>
                            </td>
                          </tr>
                        ) : modulosFiltrados.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <Alert variant="info" className="mb-0">
                                <FaBuilding className="me-2" />
                                No se encontraron módulos en esta zona
                              </Alert>
                            </td>
                          </tr>
                        ) : (
                          modulosFiltrados.map((modulo, index) => (
                            <tr 
                              key={modulo.id}
                              className={state.moduloSeleccionado?.id === modulo.id ? 'selected' : ''}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setState({ ...state, moduloSeleccionado: modulo })}
                            >
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  className="dh-checkbox"
                                  checked={state.moduloSeleccionado?.id === modulo.id}
                                  onChange={() => setState({ ...state, moduloSeleccionado: modulo })}
                                />
                              </td>
                              <td>
                                <Badge className="dh-badge-primary">
                                  {modulo.codigo}
                                </Badge>
                              </td>
                              <td style={{ fontWeight: '600', color: '#374151' }}>{modulo.nombre}</td>
                              <td style={{ color: '#6b7280' }}>
                                {modulo.descripcion || 'Sin descripción'}
                              </td>
                              <td>
                                {modulo.tipo_valorizacion_codigo ? (
                                  <Badge className="dh-badge-info">
                                    {modulo.tipo_valorizacion_codigo}
                                  </Badge>
                                ) : (
                                  <span className="text-muted">Sin tarifa</span>
                                )}
                              </td>
                              <td>
                                <Badge className="dh-badge-success">
                                  {modulo.capacidad_total} personas
                                </Badge>
                              </td>
                              <td className="text-center">
                                <Badge className={modulo.estado_id === 1 ? 'dh-badge-success' : 'dh-badge-danger'}>
                                  {modulo.estado_id === 1 ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modal de Módulo */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaBuilding className="me-2" />
                  {modalMode === 'create' ? 'Nuevo Módulo' : 'Editar Módulo'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                <Form onSubmit={(e) => { e.preventDefault(); handleSaveModulo(formData); }}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaBuilding className="me-2" />
                          Código del Módulo *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          className="dh-form-control"
                          placeholder="Ej: MOD-001, A-01, B-02"
                          value={formData.codigo || ''}
                          onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                          required
                          maxLength={20}
                        />
                        <Form.Text className="text-muted">
                          Código único del módulo (máximo 20 caracteres)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaBuilding className="me-2" />
                          Nombre del Módulo *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          className="dh-form-control"
                          placeholder="Ej: Módulo A, Bloque Norte, Edificio Principal"
                          value={formData.nombre || ''}
                          onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                          required
                          maxLength={100}
                        />
                        <Form.Text className="text-muted">
                          Nombre descriptivo del módulo (máximo 100 caracteres)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaBuilding className="me-2" />
                          Descripción
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          className="dh-form-control"
                          placeholder="Descripción detallada del módulo..."
                          value={formData.descripcion || ''}
                          onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                        />
                        <Form.Text className="text-muted">
                          Información adicional sobre el módulo (opcional)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaBuilding className="me-2" />
                          Zona *
                        </Form.Label>
                        <Form.Select
                          className="dh-form-control"
                          value={formData.zona_id || ''}
                          onChange={(e) => setFormData({...formData, zona_id: parseInt(e.target.value) || null})}
                          required
                        >
                          <option value="">Seleccione una zona...</option>
                          {zonas.map((zona) => (
                            <option key={zona.id} value={zona.id}>
                              {zona.nombre}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Zona a la que pertenece el módulo
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaBuilding className="me-2" />
                          Capacidad Total *
                        </Form.Label>
                        <Form.Control
                          type="number"
                          className="dh-form-control"
                          placeholder="Ej: 50, 100, 200"
                          value={formData.capacidad_total || ''}
                          onChange={(e) => setFormData({...formData, capacidad_total: parseInt(e.target.value) || 0})}
                          required
                          min="1"
                        />
                        <Form.Text className="text-muted">
                          Capacidad máxima de personas en el módulo
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaBuilding className="me-2" />
                          Estado
                        </Form.Label>
                        <Form.Select
                          className="dh-form-control"
                            value={formData.estado_modulo_id || 1}
                            onChange={(e) => setFormData({...formData, estado_modulo_id: parseInt(e.target.value)})}
                        >
                          <option value={1}>Activo</option>
                          <option value={2}>Inactivo</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Estado actual del módulo
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaBuilding className="me-2" />
                          Tarifa valorización
                        </Form.Label>
                        <Form.Select
                          className="dh-form-control"
                          value={formData.tipo_valorizacion_id ?? ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              tipo_valorizacion_id: e.target.value
                                ? parseInt(e.target.value, 10)
                                : null,
                            })
                          }
                        >
                          <option value="">Sin tarifa</option>
                          {tarifas.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.codigo} — S/ {Number(t.costo_hombre_dia).toFixed(2)}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Costo hombre/día aplicado a todas las camas de este módulo
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaBuilding className="me-2" />
                          Observaciones
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          className="dh-form-control"
                          placeholder="Observaciones adicionales..."
                          value={formData.observaciones || ''}
                          onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                        />
                        <Form.Text className="text-muted">
                          Notas adicionales sobre el módulo (opcional)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </div>
              <div className="modal-footer">
                <Button className="btn-dh-secondary" onClick={handleCloseModal}>
                  <FaTimes className="me-2" />
                  Cancelar
                </Button>
                <Button 
                  className="btn-dh-primary" 
                  onClick={() => handleSaveModulo(formData)}
                  disabled={!formData.codigo || !formData.nombre || !formData.zona_id || formData.capacidad_total <= 0}
                >
                  <FaCheck className="me-2" />
                  {modalMode === 'create' ? 'Crear Módulo' : 'Actualizar Módulo'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MantenimientoModulos;